import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import FloatingShapes from './FloatingShapes';
import {
  Server,
  Cloud,
  Shield,
  Code,
  Cpu,
  Globe,
  Settings,
  Bot,
  ChevronUp,
} from 'lucide-react';

const Solutions = () => {
  const sections = useRef([]);
  const [activeCategory, setActiveCategory] = useState('Software Solutions');
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observers = [];
    sections.current.forEach((section) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeInScale');
            observer.unobserve(entry.target);
          }
        },
        { threshold: 0.1 }
      );
      if (section) observer.observe(section);
      observers.push(observer);
    });

    return () => observers.forEach((observer) => observer.disconnect());
  }, []);

  // Native touch scroll is used — no override needed

 const icons = [
  { emoji: '💻', from: { x: '5vw', y: '20vh' }, to: { x: '15vw', y: '10vh' }, delay: 0 },
  { emoji: '☁️', from: { x: '80vw', y: '30vh' }, to: { x: '70vw', y: '20vh' }, delay: 0.4 },
  { emoji: '🔒', from: { x: '10vw', y: '70vh' }, to: { x: '20vw', y: '60vh' }, delay: 0.8 },
  { emoji: '⚙️', from: { x: '60vw', y: '80vh' }, to: { x: '50vw', y: '70vh' }, delay: 1.2 },
  { emoji: '🤖', from: { x: '40vw', y: '50vh' }, to: { x: '60vw', y: '45vh' }, delay: 1.6 },
  { emoji: '🌐', from: { x: '30vw', y: '10vh' }, to: { x: '40vw', y: '20vh' }, delay: 2 },
];

const techIcons = [
  { Icon: Server, from: { x: '10vw', y: '15vh' }, to: { x: '20vw', y: '10vh' }, delay: 0 },
  { Icon: Cloud, from: { x: '85vw', y: '25vh' }, to: { x: '75vw', y: '20vh' }, delay: 0.3 },
  { Icon: Shield, from: { x: '5vw', y: '75vh' }, to: { x: '15vw', y: '65vh' }, delay: 0.6 },
  { Icon: Code, from: { x: '70vw', y: '85vh' }, to: { x: '60vw', y: '75vh' }, delay: 0.9 },
  { Icon: Cpu, from: { x: '45vw', y: '55vh' }, to: { x: '55vw', y: '50vh' }, delay: 1.2 },
  { Icon: Globe, from: { x: '25vw', y: '15vh' }, to: { x: '35vw', y: '25vh' }, delay: 1.5 },
  { Icon: Settings, from: { x: '75vw', y: '70vh' }, to: { x: '65vw', y: '60vh' }, delay: 1.8 },
  { Icon: Bot, from: { x: '30vw', y: '40vh' }, to: { x: '40vw', y: '30vh' }, delay: 2.1 },
];


  const categories = [
    {
      title: 'Software Solutions',
      items: [
        { img: 'https://images.unsplash.com/photo-1516321310762-479437144403?auto=format&fit=crop&w=300&q=80', name: 'Web Applications', desc: 'Custom web apps for seamless user experiences.' },
        { img: 'https://images.unsplash.com/photo-1610563166150-b34df4f3bcd6?auto=format&fit=crop&w=300&q=80', name: 'Mobile Apps', desc: 'Cross-platform mobile applications.' },
        { img: 'https://images.unsplash.com/photo-1535223289827-42f1e9919769?auto=format&fit=crop&w=300&q=80', name: 'Desktop Software', desc: 'Robust desktop applications.' },
      ],
    },
    {
      title: 'Cloud Services',
      items: [
        { img: 'https://images.unsplash.com/photo-1451187580459-43490279c0b2?auto=format&fit=crop&w=300&q=80', name: 'Cloud Migration', desc: 'Seamless transition to cloud platforms.' },
        { img: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=300&q=80', name: 'Cloud Infrastructure', desc: 'Scalable and secure cloud setups.' },
        { img: 'https://images.unsplash.com/photo-1516321310762-479437144403?auto=format&fit=crop&w=300&q=80', name: 'Cloud Management', desc: 'Optimized cloud resource management.' },
      ],
    },
    {
      title: 'Cybersecurity',
      items: [
        { img: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=300&q=80', name: 'Network Security', desc: 'Protecting your network infrastructure.' },
        { img: 'https://images.unsplash.com/photo-1563986768494-4dee2763f6e2?auto=format&fit=crop&w=300&q=80', name: 'Data Encryption', desc: 'Securing sensitive data with encryption.' },
        { img: 'https://images.unsplash.com/photo-1516321310762-479437144403?auto=format&fit=crop&w=300&q=80', name: 'Threat Detection', desc: 'Advanced threat monitoring and response.' },
      ],
    },
    {
      title: 'AI Solutions',
      items: [
        { img: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=300&q=80', name: 'AI Automation', desc: 'Automate processes with AI.' },
        { img: 'https://images.unsplash.com/photo-1516321310762-479437144403?auto=format&fit=crop&w=300&q=80', name: 'Machine Learning', desc: 'Custom ML models for your business.' },
        { img: 'https://images.unsplash.com/photo-1610563166150-b34df4f3bcd6?auto=format&fit=crop&w=300&q=80', name: 'Chatbots', desc: 'Intelligent conversational agents.' },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-950">
      <section
        ref={(el) => (sections.current[0] = el)}
        className="relative min-h-[850px] text-white flex items-center justify-center text-center overflow-hidden opacity-0 py-20"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-gray-950 to-blue-900/90 z-0"></div>
       {icons.map((icon, index) => (
         <motion.div
           key={index}
           className="absolute text-3xl sm:text-4xl opacity-40 select-none z-0"
           style={{ left: icon.from.x, top: icon.from.y }}
           animate={{
             left: icon.to.x,
             top: icon.to.y,
             opacity: [0.4, 0.8, 0.4],
             rotate: [0, 10, -10, 0],
           }}
           transition={{
             duration: 6,
             delay: icon.delay,
             repeat: Infinity,
             repeatType: 'reverse',
             ease: 'easeInOut',
           }}
         >
           {icon.emoji}
         </motion.div>
       ))}
       
       {techIcons.map(({ Icon, from, to, delay }, index) => (
         <motion.div
           key={index}
           className="absolute text-white opacity-30 z-0"
           style={{ left: from.x, top: from.y }}
           animate={{
             left: to.x,
             top: to.y,
             opacity: [0.3, 0.6, 0.3],
             rotate: [0, 15, -15, 0],
           }}
           transition={{
             repeat: Infinity,
             repeatType: 'reverse',
             duration: 6,
             delay,
             ease: 'easeInOut',
           }}
         >
           <Icon className="w-6 h-6 sm:w-8 sm:h-8" />
         </motion.div>
       ))}
        <div className="relative z-10 px-6 sm:px-10 lg:px-24 max-w-5xl">
          <motion.h1
            className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-8 text-shadow-lg text-white"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Our IT Solutions
          </motion.h1>
          <motion.p
            className="text-lg sm:text-xl md:text-2xl text-gray-100 mb-10 leading-relaxed text-shadow-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Explore our comprehensive IT solutions designed to empower your business with cutting-edge technology.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row justify-center gap-6 mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <a
              href="contact"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-7 py-4 rounded-lg shadow-xl transition duration-300"
            >
              Request a Consultation
            </a>
            <a
              href="#services"
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-7 py-4 rounded-lg shadow-xl transition duration-300"
            >
              Explore Services
            </a>
          </motion.div>
     
        </div>
      </section>

      {/* ── Professional Solutions Section ── */}
      <section
        id="solutions"
        ref={(el) => (sections.current[2] = el)}
        className="relative py-28 bg-gray-950 text-white opacity-0 overflow-hidden"
      >
        {/* Background accents */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
          <motion.div
            className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(37,99,235,0.08) 0%, transparent 70%)' }}
            animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(124,58,237,0.07) 0%, transparent 70%)' }}
            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.9, 0.5] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
          />
        </div>

        <FloatingShapes />

        <div className="relative z-10 container mx-auto px-6 lg:px-16">

          {/* ── Section Header ── */}
          <div className="text-center mb-20">
            <motion.span
              className="inline-block text-xs font-bold tracking-[0.25em] uppercase px-4 py-1.5 rounded-full mb-6"
              style={{ background: 'rgba(37,99,235,0.12)', border: '1px solid rgba(59,130,246,0.25)', color: '#93c5fd' }}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              What We Offer
            </motion.span>
            <motion.h2
              className="text-4xl md:text-5xl font-extrabold text-white mb-5 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Our <span className="text-blue-400">Solutions</span>
            </motion.h2>
            <motion.p
              className="text-gray-400 text-lg max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              End-to-end technology solutions built to accelerate your business, improve efficiency, and drive real results.
            </motion.p>
          </div>

          {/* ── Solution Cards ── */}
          {/* 
            ╔══════════════════════════════════════════════════════╗
            ║  👇 ADD YOUR OWN REAL PROJECTS HERE                  ║
            ║  Replace any of the items below with your real work. ║
            ║  Each item needs: title, tag, tagColor, img, desc,   ║
            ║  accent, glowColor, file, number                     ║
            ╚══════════════════════════════════════════════════════╝
          */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              // ── YOUR REAL PROJECTS (replace these first) ──
              {
                title: 'Custom Software',
                tag: 'Development',
                tagColor: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
                img: 'https://i-media.vyaparify.com/vcards/services/6217/Z20.jpg',
                desc: 'Tailor-made software built to streamline your operations, boost productivity, and scale with your business.',
                accent: 'from-blue-600 to-blue-500',
                glowColor: 'rgba(37,99,235,0.2)',
                file: '/pdfs/custom-software.pdf',
                number: '01',
              },
              {
                title: 'eCommerce Solutions',
                tag: 'Commerce',
                tagColor: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
                img: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=600&q=80',
                desc: 'Complete online store solutions with secure payments, inventory management, and seamless user experience.',
                accent: 'from-purple-600 to-purple-500',
                glowColor: 'rgba(124,58,237,0.2)',
                file: '/pdfs/ecommerce.pdf',
                number: '02',
              },
              {
                title: 'QR Solutions',
                tag: 'Smart Tech',
                tagColor: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
                img: 'https://images.unsplash.com/photo-1595079676339-1534801ad6cf?auto=format&fit=crop&w=600&q=80',
                desc: 'Smart QR-based tools for digital menus, access control, product tracking, and customer engagement.',
                accent: 'from-cyan-600 to-cyan-500',
                glowColor: 'rgba(6,182,212,0.2)',
                file: '/pdfs/qr.pdf',
                number: '03',
              },
              {
                title: 'Restaurant Tech',
                tag: 'Hospitality',
                tagColor: 'bg-orange-500/15 text-orange-300 border-orange-500/30',
                img: 'https://images.ctfassets.net/2d5q1td6cyxq/4uMtf4ZJfCE77Y0EXMmn9i/8234168b1a3b8c990437ef87418ff52d/PD05513_-_AUEN_handheld_pos_tableside.png?w=708&h=586&fm=avif&q=85&fit=scale',
                desc: 'Tech-enabled dining tools — from table ordering systems to kitchen displays and billing automation.',
                accent: 'from-orange-600 to-orange-500',
                glowColor: 'rgba(234,88,12,0.2)',
                file: '/pdfs/restaurant.pdf',
                number: '04',
              },

              // ── SAMPLE / PLACEHOLDER PROJECTS (replace with your real work) ──
              {
                title: 'ETL Processing Platform',
                tag: 'Analytics',
                tagColor: 'bg-green-500/15 text-green-300 border-green-500/30',
                img: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=600&q=80',
                desc: 'A scalable ETL system for processing and comparing JSON, XML, CSV, YAML datasets with authentication and role bases access.',
                accent: 'from-green-600 to-green-500',
                glowColor: 'rgba(22,163,74,0.2)',
                file: '/pdfs/dashboard.pdf',
                number: '05',
              },
              {
                title: 'CRM  Analytics Platform',
                tag: 'Enterprise',
                tagColor: 'bg-pink-500/15 text-pink-300 border-pink-500/30',
                img: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=600&q=80',
                desc: 'Complete CRM analytics software and secure role based user management.',
                accent: 'from-pink-600 to-pink-500',
                glowColor: 'rgba(219,39,119,0.2)',
                file: '/pdfs/hr-system.pdf',
                number: '06',
              },
              {
                title: 'Billing System',
                tag: 'SaaS',
                tagColor: 'bg-yellow-500/15 text-yellow-300 border-yellow-500/30',
                img: 'https://media.istockphoto.com/id/2182110555/photo/financial-report-and-banking-management-with-e-invoice-bill-digital-nline-statements-c2c.jpg?s=612x612&w=0&k=20&c=QmEXFY-ackuW4RtlABZxHsR6LCcPcxv8Nql-reTY1w8=',
                desc: 'Online billing and invoicing system with automated payment processing and detailed reporting.',
                accent: 'from-yellow-600 to-yellow-500',
                glowColor: 'rgba(202,138,4,0.2)',
                file: '/pdfs/booking.pdf',
                number: '07',
              },
              {
                title: 'Cricket Application',
                tag: 'Operations',
                tagColor: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30',
                img: 'https://www.shutterstock.com/shutterstock/photos/2733313255/display_1500/stock-vector-dynamic-cricket-bowling-action-with-player-and-batsman-illustration-2733313255.jpg',
                desc: 'Cricket management application with team management, and match scheduling, tournaments and player handling.',
                accent: 'from-indigo-600 to-indigo-500',
                glowColor: 'rgba(79,70,229,0.2)',
                file: '/pdfs/inventory.pdf',
                number: '08',
              },
              {
                title: 'A Furniture website',
                tag: 'EdTech',
                tagColor: 'bg-teal-500/15 text-teal-300 border-teal-500/30',
                img: 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wCEAAkGBxISEhUSEhIVFRUVFRYVFRUXFRcVFRUWFRYWFxUVFxUYHSggGBolHRUVITEhJSkrLi4uFx8zODMtNygtLisBCgoKDg0OGxAQGy0mHyUtLy0tLS0vLS0tLS0tKy0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLS0tLf/AABEIALcBFAMBIgACEQEDEQH/xAAbAAABBQEBAAAAAAAAAAAAAAAFAAEDBAYCB//EAEwQAAEDAgMDBwULCQgCAwAAAAEAAhEDIQQSMQVBUQYTImFxgZEyUqGx0QcUFyNCVHKywdLwFRZTc5KToqPCJDRDRGKCw/Ez4TWD4v/EABkBAAMBAQEAAAAAAAAAAAAAAAABAgMEBf/EACoRAAICAQQCAQMDBQAAAAAAAAABAhEDEiExURNBIjJSYYGx8AQUQnGR/9oADAMBAAIRAxEAPwCw3blE/K8QR171MzalM6OB7wsmaFu7+hp+1M/Cet3oC87c9HSjZjHN4rsYpq8/xmDLWZwfxZSu2RVbSFZ1RoDohskvIInNAHkxv3aJN0JpI3wxDV1zwWAbsqtzYql7GguIaH1GszAAEvBdaLgRrfTVOMEcuYV2G0/KBm9rt/HWixfHs33PBLngsCcG/NlFVrrgAh1jJjhbvhENnbAq1iA2s0SATObog5tYB80oboPj2a/nQn5wIJh+QmIcHHn6YAIAnMc3EgAbjIUlT3P8QG5vfVEixBBdBB36aITbVi+PYX5wLoVAhNH3O8S4T75pC8Dy7nqtdT/BlivnNL+P7qtRk90hOUOwmx4UzXBBvgyxXzml/H91L4MsV85pfx/dVaJ9C1Q7DoIToF8GeK+c0f5nsS+DPFfOaX8z2J6Z9CuH3BxOgXwZ4r5zR/mexOPcyxXzmj/M9idT6D4fcG0pQQ+5livnNH+Z7E3wZYr5zS/mexFT6D4fcGyUpQT4MsV85pfzPupfBnivnNL+Z91GmfQXDsNyE2ZBfgzxXzml/H91L4MsV85pfzPupaZ9BcOw3mCbMEE+DLFfOaX8z7qXwZYr5zS/j+6npl0Fw7DZeExeEF+DLFfOaX8f3Ux9zHFfOaP8z7qNMug+HYazhctrNNwQRxBlBz7mGL+c0f4/urn4MMX85o/x/dRpn0Fw7DRqBQVcQAhZ9y7F/OqP8f3VluUOxK2DxLcM+q15cwPlsxDi4RcTPR9KlqS5Q4qLezN23Ehdc+gfJPDTTd1PcPBaBuHQrYnsznnOop1JzSSKYjCFtu4/UphO8XPa/wCxOdD3/wDGEnb/AP7PWFlZ0UcY2n8Q/s+wIM90OgiWljZb5xDDEN3nMW8bgyLLQYsfEVOw/VCl2SWc3mikX0mtNNtSYzmHZm5LuMAWMndHSU665IyLYjq4N7HkPczoszAsgHLT6IY8RZ/Sd0RlJ1lE8Ps9tMBhJGUZiREB3Na5rXJyGC7VrtBKIcn6VWq6MRzVQPlwDDLQ5sQHBwIYyHC0QZBggojyh2y+jTDG2MxLhcMnLLMze2+4jSCAq8cjGzI0cM81GNsRUh1pcZAOrRqZubx6kZpOp03PYKR+Mb0ruLoaZAa6BBkjpR8nsU2yMbkwjXOa0AVnB5ynMQRVdZ4M+cIsehA1CqbWrixa12UjowTlyvBbYxZsCRe4ZawCqSA0Oy9pAsBcbXD6Yu1mhY2IMvIiSSSOqQi2FxjXUC4ua3d0XdEdKxLlhaGMyMAZUjPJp9FuVxkZcxuWgXb1DuWo2NVq+8ySRn6UAgXkgTljUyojtf8AoTCeExwBlkEOdlnLGY6gAmBESTqbhGXVwBJJ4afYsJszC1mD4x1R0PJByObpJymJLnSSSMu82Cv4bbdSaj61F9KnLWUnvYQ2+bM7JcmBBuBo7sWmGbVomSNi0yElDg8U2o0OY7MDviJ64U67YtNWZsZJOkmISSUJ0AMknTIGJMnSSAZJOkgBkk8JQgBJJQkgBJl0mQA4C8h90ds7VZ1UGet/tXr4XkXugX2sP1DPt9qxzfSbYfqLPI9vxb/1tT1o/CC8kG/Eu/W1PrFHQFMeCp8nBanXUJkxHnR39p+swJHf2P8AS9PF+/8A5B7Eot3H6y5GzrJcZ/4avf8AVC72fsI1aVNwLWS2RJ8qd95k9HgIEXvbnED4mr/u+qEJpVGOLMwYBTDS8knNALSHCJcIzRAyjr1U03wZ5FsajZu1X0WZaopUwILHjyJaS8iWG+ugAHlQbAKvtLGvrZCHtD3NcJYwMa7UlrcpuOlE77TF5zuJo0RlAe9rnOJu0spZZg9AtaTNxZw+wntmbPp5WFz3ENALWiJbnyyIEmfC60i6RjpOqbqrHBpJEZnPa4NZByFkkRYXMa2MbyDexhfWOYnnCS5rZmBYRnaNATmEyIgdahewHK4uDHFrrkMc2w4npQIbNuO/WlTwrhmdmuw+UHSHFok+SbGN0ju0SvUFBzZODqhuRobUFOXCRlbIHkgETaXXMdp0RylQq0cO7MT0dbEXzSIi4g21myxWz9qlrwKwIynMMzQIIBE5TcDTQierfrNpVx7xqAPdUaabpLAdC0SGua6dCRMynGMrafRLImbQJDaL7ucA85jDi52jW0yQB5ThcWLdN6tVXVntfRBOZ0EPLQMpGU5QHbzB7AdZNsphKhdVNRgDGsG7nAAGtNg5zjPSd8rQ6WkIxh8YWlobUhhaJJcATmBNLoh3RF4ns6lzqemX4KNPsQVqLBzjnPaGtboJkXcTB1ku0tojtHFZhMHQencvNsHjXnO/n+dIJzMZUJDXR0XAnUWAgnSddBp9jbVNUhgB6AFwJkgXPESTHsXTiy1sQ43ualjpEp1zTmBOsCV0u5GQkkkkwEkkkgBJJJIASdcynlACSSlMgB0kxKjdWAQBKkq5xQ3D/wBKs/azBYXdwF/V9qTY6CK8j5c//Ln9TT9QXoFbbjswaKTgD8vowDuGs+hebcpnl21CTrzbd87gscz+JthjUg3yPHxB/WVPrFGig3I8f2YfTqfXKMuKUfpQ5fUxkkkyBHnwF+//AJD7F02mY7h6yrmEoSe/+t6uU8L/AE/auGU6O5RBmJpkUavY71LMteQ+3+nzrWE2B4SD1FbfaNGKNTsd6llMgLQAHZrX6JEWOkSPE9yrHIzyIu1qrH0y5znGoHA03FraeZrYGTM3yXNbcE6ab0e2Ph3VWBxaYLQYlxDQRFjJdI4m9+tAMFg2ODjJHRkzA3H5VhEweNj363k8x/NNGYHJ0c4Dgd/SMgEm0yb6QmqpowkgZtKgWAEZZLi0NLXOzNvo0gnLNvwVNszDPNJxqOANjENkmLBwaNd3S0uNZUPK3EPytYTbNMC14vfirGxSTh3ukxcAmCf2hqVpW1k+wftOpTdUZNDKW0w13NhuUvM/GGBM+SfG10a2dhnnBVmtbGY1G5bCzoGnm304LNMxgsape6G9AAkaFpO4DKYvF48UWfjC7AVXUzkIbUyw4w224kTEm0ytY22Zsq4SoG02ZW0MrXNa8gh4Muu1wmzQQLw6I13K7tPDMfSecPUmbXa6nALc8ZcuZ2Yhg1uAZFiFiNibQdRzZnBgLDlaWhzZyQ0yCJMGwcddbo7sjY+NxVUsZVaA0Av5xtQBpOfoxAM5pOU5TeRGpmUb2SGnRb2Pg8rHOznKzouc0QGPLnAkAt6WouZgmwC3vJimYYWE2HS6LRmJa27soAFuA4d+YdyXNZxZXpw/o/GAkNeSSQ0yelE2AAgiZ0A3OwcE6hTFMmzSQ3sm28/grPFjblbHKWwXbZdSoc6bnF6BiTSlmUBqJs6AJ865L1DmTFyAJecTc6oiSguP25RpmC/MRqG3jv0Suhhx2JXHvhY6tynJe1lKmHF2mY3PcOziuX8qKlMNL6ZbnnLIuQDBIGuphS5xHTNmKxSNU8Y71mTtnPTztZVyyA5xADQZg2sdbQVJUqkMzGTDmgBrRcOOUx3kKfLEelhqtiesHvn/ALVCviHn5ZaJ3Bpnq0t4oZicWQW9ECQYDTniNxDRY9vFUcTjS1kw8Aw4ghgIkwLON5IOgKTyopQL20sU9rHuot5yo1ji1rnF2ZwEgdqyjeUGKoZH4jBgVa+ZvxZLXEMykZmXh17X0BmIR3BPNXMWtcMuUw913CQbdEZdW9V7wLq9ghnpEVmhmV0XgkHWZgNg6WnQi6h5aHRRw/KrDuqOowRUbcy0m9pEiWyJjhwWX2nX5zHmoAQCwAT/AKQ0FbTG7NoS4tptDwD8kDO7JDJdIzAOganh2YPEF3vtwcCMrdCwU4nL8gExIg6mVnKakjTFya/kgP7K36T/AK7kVcUK5Kf3Vh4l31iPsRQrVcEPlilJclJIDO7NZMdo+s9EqdH+n1FVdktsO7+sotTp/wBP1V5cnueiC9p0vin/AET6ists4tAcSwkw28gNgCYk6O4X3aG4Wy2oz4p30T9q8/dUMgboFt2gK1w+7M8itFyvjH1CGtEGIaBOo0vMzEcfsXofJ3ZL2YZrarcjoh0BjWghxEkAa3v2Beb4V7gZYOm05g4XcI4DRbXZ22alenLnAA2yAEEBpIEjdvJ/AXTCMVE5sl2kitjsJnqedkuG2Oc5gAA0kTrp2I+zAilRLcoptknIcpcQdG5h0eMgDxWar7WdSrZWFocQJJMHKTLgDx046Laua6rh4cGtqObPnNB79e1dEI3Exk6keU7Xq06hdzbsoDrU3OBMkDMWuAAIndrbTUohs6jUqUferWAGq14DnGAJbOkXsFbbyMc55LnGM5lwt15miLzuAWzw+zaAObI0mxBi4iNPN0H4KqO7pEy2Vnhe3MM4VnMHSLAQ5zcgnKJc4R5Y4HU23r0v3NcAx1Jj6jH5iS5hlwbYDpdHWTNz1haDGcncNUqNqFsOaXOhtmuLtS5uhPWieEpZGhoi2lgIG5o6gq0bi1qgicusb50GvFPzgQvFbQbTgOdBOgEk9sDcqlXbzW+S1zv9wHrKcpxjyyUm+A9zoVbE48NMAFx1IG4dfBZzG8o3bugDadZM7naIRXx+9xdfUzPieyVjPOuImixv2bCvtgtElgj6V/DL9qrjlI3fTfPVEHs3rJnbI8kdOBffbjlm3jGvBc1udyOqNqZGggTIHlSBECdetZrNL2V40ah3KOo7/wAVCY1kk9ejQq1TbmJyknm2kRbogw7ySGudPoWXdTgkvqCNTEk9l26/jgoKWJoteC9xe0zvg7zZ0XFwjyt+w016DeJ2hWqQH4iA6LMdPR62t0N9EQOwabmyTUeGm+QWNtCPYslSxznVHuHRaSXNa1jKkMaQQ7PVAvAkgjU6hHqfK+kGEsfmyubmBvAmDmvJOlxvtpc5ymx0FcNRfSpltKkQ0HM12YODs3laaWnXeAOtS0tuN5xrHw4m7XEAXJAdBniACBvQRvKGhUGXM5l3GRmElzQCYiCDmfM7zPWMbtPaVVlUtLzDXZ2jMcjxvDZMgkEi8T1GFEdUnsVR6tV2pSaSZJBO7USGzPf33K4w2NpVSGug+S6ZA6TdHDeDJNu3sWDwG3aFQiXOY/mxmEGHObABkEzYXJjSZWg2XjaWSI+Ve4GsyACSTIPGDYKnGRIsRSqNGIbSFNrGh0gMLOi2BnBzRZhPWZ6igZr1HMiqQycvlNAfkyjKGHKS21wO1Fa20mtq5g9wkSRuJk5R0hYERbUEkIHj9sl3xhLYe64IJAAsBltEACypNsDXYTFtYGN+NLRlc0tksbYyMrdBrIMwY3QqbK4qE5Ks5WkjKG9Jp3OkEmb6Wm1tCEfiwXsLHmzgczZbOsTe19yqbJrGQ95BAILZkEnqcCALxY9VrIlB8iTNFicaGUssjnGy9gcCzM0iHtaLtzB14B3RHHGYfEmpiC9xJJbcnW0D7Eb2iab3fFVHc2Ye3e6k68iT0hBItN78BASlSyYh7QZyjXr1OnWk0lH8muL6jdclR/ZafY76zkSKHcl/7rS+j9pRErdcIzfJykkkkAK2MOiOz+kowPtHqQTYx6I7B9VGo/HcvJfJ6JS2p/43fR9q88DoM/6Y46gBeh7UIFM/R9q83bTqPc0U2uMwJDSWgkgAEgLoweyMjpF1rKRhtQuYJGYjc2JMDitVswtDLFoY2zcpkQN8wPV1ISdkms4UmtYx4MF1w08QASSI4dvC53Z+xG02MYXOeIMNAgGZkudERJ67LdT2MJL2XMCzDPBz5XukFvR6QgAAhwvHVZFKj2uaG0yBAJgHLIy9ENNra3HUqHvKt5DWimwa5IAI+lMuPae5BuU+Lo4To86XVHTFMRmAJnM5w8kX7Se9X5ZSWlGKgr25Coxj5EEWgARPXGgPcFXO1qjSS4u13t6MH6UeglY2jtUvdLwTPX9ij5StDWsqMMSYMWm0jvsmoP7jf+3fs9P2ZtHOQHMsflCwkTNpMadassrGo112sO7Ldwv1jWCOxeA1MY7zz4qu/HP88+Ktxm/8iH/TpHueK2hSeclVpyxMluUiwi5vpOvm9SpgYSqJp1MsHI6ZawnNBkEy0z/1deQYPlLXoA5H+VIveJBGYcDc3VX8p1DfnD4rJf0073kPSj2DHYM0mPIDSGsBAZnEPk+awl27QDXghA2lVpyWsuTJylrXENbIfBbJsTv36Lztm0H+efFTjHP88+Krwdv+f9LWP8m32ltJ2ILJpZnZh0aTW5n5mgtDi0HNvMiPJ8aTcQ6QxxfSbBzNdScSTFyC8gk69km6zNHHPkdM+Kgr7We57mg5QD8m0nieu6fiE8dGwwuzKlV7WNNNtzJJL3tykG13XsOjI0K72jgW0KrWXc1pcXvDJZDgHRO4yRY8eMBYR+PqeefFN+UKnnnxT8L7JcTfbHwLapBl4pXa0c6ReQbA9ujtZntgr7ONNppkNrZp6YaM7dBTzkOEAjLJB3N3wsP+UKnnnxVjD8oa1IHK/W17xNiRwKTwS9MWk1OFxNPMxnNybsbGdz8nlNDgImCA7MCNTIN0K5QtmpIYGgtExcSLDRzgDAG/UygZ2nUP+I7xXJ2nU/SO8VccNO7DSXcM0EHOH2AIygRqMxd1Rbv6oOv5KYmmQ9rwSGgZSYzEHdJ0iJmIusE3alQkDnHXIGvErTv20KYAALjGk+slW42Jxs1tSoxhN2xoCNDoR9iqYmvRcIMGdRFigFLlEx9qjCOBJkWFhY2HYpqzjq2wOgsTBHFJWtjKUWibC1MlVjab3lmYQ1xJa3iL2jqXODxgAILWwYECQCdQSdAbQDO9VBUf5zvH7FE6ZlDjZNmhoYlgdnDS0kZSCYDpES5ws7tAGuh1QPZ5+MdExltOsTaVLTrWiN8i5t1Ktsx5L3E+a1Y5I0jbDyej8mx/ZaX0EQcqHJ3+60foBX3LVcEPk4lJNCSQAnYvkjs/pCK42uGNc47p9SE7H8nu/pYr+2cO+pTcxjSSSRA7d/ALytNyo9FulZmNobSdUDuwW8VU5O7TxOHphrcNLgSc+drXQY6I4C3sjfpsPyVpEfHZnO6nOaBbdBE96uU+TGF8x37x/tXasDo5pZYvkzZ2lVL2uOBs0ENYKoa25vMXO7fuVunt6s0gtwEXkjnyZtESTI4/9o+3kzhfNf8Avan3l1+bOE81/wC9qfeVeGb6JeTH0/5+oFdyuxQH9yDQODxoOPGy8/Nd1ao+tUMuqOJPVwA6gIA7F6weTWD3tf8AvantXlmIwbsPVfQfqxxg7nN+S4dRCuGNwRtglBy2O2nIQTYehQ7TpVcQWgPysboOJOripKz80TuXEKk6OmUdRQPJyof8YeCjdyWqfph4ImumG6rWZvAmZnaWwqrHspzmLjY6ARqSrjeTNQf4gR6vi6bMQznafODm7XiCXanjotHgNoUHA5aEBupFMEC06gJTzaVujHw02YEbAeP8QfjuXX5Ef+k/HgvRcBtLCVyGsLSSC6DTOg11bCA8qa2F6VOnSio1wl4aGNEa2B6XeApjnUnVDWNmaZsZ36T0BC8Dseo975dlhxBOsniiBsbFVjiHEmTv9i3TIlHclPJx36YeCX5tv/ShRz1pT1osWg7/ADaqfpR4KptPYVVjc05gCJjdNgexXGVDxV/CvkOB3tM+BRqofiTBVHk+8tB5wDqhJ3Jt/wClCs4GqXiSdwT12BGoPFtYPqcnnjSo1dc4QfjInSdx61MuS0J3ZKjXB01yO7CNR4LGML8sGxAyg9vX60BAXpPuc8mWPour1w74wgUgHOZ0GzLuiRIJNvozvUtOtgm41uChhcR83d+0z2qJ2CxHzd37TfavR/zXwnmv/e1PvJjyXwvmv/e1PvLLTk7Rlqx9Hmj8HXH+Xd+032qrgKFRjnZ2FoygCYOk8F6i7kvhfNf+9f7VBU5K4XzHfvH+1KUJtbscZwXCFye/u1H9W31K8V1hsI2mxrGTlaAACZgDS+9M4LSqRle5EknSSGDdgUCWgmw9Js3TwK0tOqAIAUQwUaIDyt24MGwAAOqvnI06ADV7urq3nvjGGJRextKbnsaGvjmsGZ7mtHFxAHiVVo7bw7zlbVpuPAOBns4rxXH4+rWfnqvc93Emw6gNGjqCjZiSFtpBQXs9359vmpucYdxHevKNlcsq9GAXZ2+a+/gdQtjsrlrha0B/xTv9V29zx9sI3JeN+jRuYw/KI/HYgu2eTVPExmeZGjgBI7+HUjOcESO6DZD6tW95HVZZZJ6UPGm3sAfzBHzh37A+8l+YQ+cO/YH3kaz8PVHqS99vGhPiftWPlZ0XPsC/mCP07v3Y+8nbyCAN67v2B7Ua/KFUb/UkNqPGuXwIS8zHc+wBifc5pvqc574qzAaBDC0AX4cVTwLGUDiKJfJFQsbOrujGg3rXDa/EDh5Ua9oWMw+NIx9Wo1meXutqelF2xv8AaVM561THBNOzjkjsOtTdTqOYWg03hwdALSXQ0RrcCdN6JY3kPz1R1T3wWl5mMkxO4Xui+H2vTdGZr6ZInK9hBHadFcbiKb7BwPUqi0parJblRlvgwJ/zR/df/pRH3LOGLPfTHtW2p4rKID/Ez3XXYxhNs/cD9gW/l25/Yxak3uYJ/uXVN2IB/wBsKu/3N6o1qO7mtPqK9G54ghswXSADYmNYVhuYa9Vu3RT532PSzzNnufEa1H+DVZbyDGVzTUqDMIkBgIB11XpXOje0b9/DVP0PNK0WWL9kXNHmFH3OwwQ3EP8A91Np9RC5qe568/5nxpx/UvUW0WO0T+8wtE73TJ1yWx5Ofc4qfOB+z/7XJ9zmr+l/hB/qXrBwXWozgexPcWtnmuzeQnNvDqs1APklsN7xv7FvsMSAPVpCt+9HD/tUcftzD4Z2StXax0TlJLnRu6IBI70KyW7LgcU+dAa3LbDDyc1TsaAPFxCHYjlwT5GHb2ucT6GgetMaxSfo1jqihe9YPFcrMU7TKz6LR/VKo/ljETm558/SMfs6ehBfifs9I5xcucs1sLlHmcKeIgTYVAIE7g8bu0eG9aw4I7nD0pEONclMvSVk4F3V4pJCDK8P5V4818XWedA8sb1MpktbHbBPa4r2D3yeK8T2tRLK9Vp1FR4/iMHvEHvUxNcaKZChrOhTlUq5urNTgElXcMqjArVK0JsceTY4DlHWpUm02lpAmC4EkdWuil/ObEE3LCOBYI9vpWZZWU7HKHRehN2kbPA8oKL7VGmmeIJc32hGmUmPGZryRxBaR4wvOA0qbDYirTMscWnq39o3rNwh0DxS9M22JY5vEjj7VWNQnep+Te1jXBZUjO0TItmGhMcdPFFamEYdWjwv4rGWDpkeVxdSQAdKh5toMxfiLHxRyps2nw9J9qp19mebPeSspYJGkc8ChVqkiMzjcWJkW/7TYSsab8wMHKRMTvXb8HGsj8eBXfvP/UsHFnTGcf0FtPHmoGNcWnpiIseGh7VPsuA4u4OOmuih94nzvR1/gqfC0C2ZdMmbCN3b+JSUWEpRqkEX12uxNAjNALpJBESy14jii72ydd49M3/G9AGt6z4+hShx4nxVKLMp6XVBgN+z0mPQnE/jtQptQ8SpG4g8VaTMnQRI/HfCkbWdx8e2LoYMUeA/GqFcqtvuw2Gc9gGckMYTcBzt8dQBPgrjrul+5LSfJqnYogS4gDibDWNVRr8pcMzyq1Psacx8GyvB6+PqvcXve5ziZJJJPinZtB43yu2OOS5kZOMej2LafL/DspvNJr3PDXZTlAZmi0yZiY3LxfEV3vc573FznEuc43JJ1JVv8ogiCFSkbjK1SoIpLguYHEkI/hyCAVlaRgrQbNqbu/8AHoQzaLCzaQKq16OVXKLk+LbIUWaNWDMy9K5DYz3xQLXHp0iGnraRLT6x3Ly8uW39yl556uN3NtPfmt6yrOXKvibv3oeISRCEk6OWzIuqrJ8rNhmseepAZ4h7dM4GhB84C3WI4XP1KipYioVzXR0I82qUy0kOBBFiCII7Qh1VbXbGE5wydRod6yu0cE5hmLcVtGVmmqyq1XBDgOpUGuVqg5Wyo8hLDsCv0gEOw7psBJRjCbNxD/Jov7xlHi6FkzoTS5OmlcvcitDkviXa5G9rpP8ADKuUuRjj5db9lsekn7FIPLBeyvyNaTWJGjWGT2kQPX4LZKLZey2UWZGCBqTqXHiSrhpJo4cs9UrK5auHMVvm1yaaZmUi1RnDN80d1vUrxpJubSaTGpNFE4Ru6fE/anbhG8T4q7kSFNT449Fa5dlUYRvE+K7bg28T4j2KwGLoMTWOPQtcuyv7ybxd4j2JjhG8XeI9islhXDqZVeOHQtcuyqcO3zneI9iz3LbZhq4VwplznMcHhtjIEggAC5gmy0r6RVPFggJaYrehqb7PEA5Otttbk0Kji+4cbkjeVnMXyfrM06Q8CtFJMvUCyE9JgEnene1zTDgR2p2qilTOhqjOzjohNJklHcBROqlmsEEqLl1iavRXAgdvAXPgE42dXrHo0yBxdZSlZcskYrdgolen+5lsw0qL6zxBrEZRv5tswe8knshDth8j6IIfWJqEXyQAyevUu9C2rXLRI4smVNUgjmSVGUkzAyr6SgfhkQIXBC5qN7BFTZ0qrV2E1yP5VzlTCzJv5GUSZMjsMK7g+TGHZ/hh30pd61ociWRO2FsgwtJjLNYG9gA9Su06qiyJ8iVBZcZWClbWVANXTUUKwmx4UgIQ1ripW1SqoRfyhIsVMVlIK6KAm5tMaS5bWXYqpgcGkmNJTc4F1ZAitkSDFZyhPkQBVIShWTSXJop2BXhRVaYKtmiuTSQANdg2nUBQVNk0z8lFjRS5lTpHZm8TyWov1B9HsQiv7nVE3a97eyCPCFvWsXYYmkGow2B9z6i0y5739UgD0CfStNg9g0GCG0m94k+JRcNC6a1Oh+RlVmzqXmNHcFMzAU+CmATppENjNwg3FP73PFPK7DkyTjmSkpecSTAzLlykksDUYpkkkAOkkkgQ4C6CSSYCXQTJIA7CcJJKhHQKcFJJADgpwUkkwOg8roVSkkmI6FZStrpJIAkbXXbaqSSQHYcnskkgBZUsiSSAGyJsiSSYh8iQakkgBwE6SSAEnASSTEKEkkkAf//Z',
                desc: ' furniture website for product showcase and order through whatsapp .',
                accent: 'from-teal-600 to-teal-500',
                glowColor: 'rgba(13,148,136,0.2)',
                file: '/pdfs/school.pdf',
                number: '09',
              },
              {
                title: 'Blog Platform',
                tag: 'PropTech',
                tagColor: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
                img: 'https://img.freepik.com/free-photo/online-message-blog-chat-communication-envelop-graphic-icon-concept_53876-139717.jpg?semt=ais_rp_50_assets&w=740&q=80',
                desc: 'A modern blogging platform with rich text editing, media management, comments, linking, and admin dashboard.',
                accent: 'from-rose-600 to-rose-500',
                glowColor: 'rgba(225,29,72,0.2)',
                file: '/pdfs/realestate.pdf',
                number: '10',
              },
            ].map((solution, i) => {
              const message = encodeURIComponent(`Hello, I'm interested in: ${solution.title}`);
              return (
                <motion.div
                  key={i}
                  className="group relative rounded-2xl overflow-hidden flex flex-col"
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.12 }}
                  whileHover={{
                    borderColor: 'rgba(59,130,246,0.35)',
                    background: 'rgba(255,255,255,0.05)',
                    transition: { duration: 0.3 },
                  }}
                >
                  {/* Glow on hover */}
                  <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-500 rounded-2xl"
                    style={{ background: `radial-gradient(circle at 50% 0%, ${solution.glowColor}, transparent 70%)` }}
                  />

                  {/* Card number */}
                  <div className="absolute top-4 right-4 z-20 text-xs font-bold text-white/20 select-none">
                    {solution.number}
                  </div>

                  {/* Image */}
                  <div className="relative h-44 overflow-hidden flex-shrink-0">
                    <img
                      src={solution.img}
                      alt={solution.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-108"
                      style={{ transition: 'transform 0.7s ease' }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/30 to-transparent" />
                    {/* Tag */}
                    <span className={`absolute bottom-3 left-4 text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full border backdrop-blur-sm ${solution.tagColor}`}>
                      {solution.tag}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="relative z-10 p-5 flex flex-col flex-1">
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-blue-300 transition-colors duration-300">
                      {solution.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed flex-1 mb-5">
                      {solution.desc}
                    </p>

                    {/* Divider */}
                    <div className="w-full h-px bg-white/5 mb-4" />

                    {/* Actions */}
                    <div className="flex gap-2">
                      <a
                        href={`https://wa.me/918141581103?text=${message}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex-1 text-center text-xs font-bold py-2.5 px-3 rounded-xl bg-gradient-to-r ${solution.accent} text-white hover:opacity-90 transition-all duration-300 shadow-lg`}
                      >
                        💬 Inquire
                      </a>
                     
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* ── Bottom Stats Bar ── */}
          <motion.div
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            {[
              { value: '20', label: 'Projects Delivered' },
              { value: '4+', label: 'Years Experience' },
              { value: '98%', label: 'Client Satisfaction' },
              { value: '24/7', label: 'Support Available' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                className="text-center py-6 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
                whileHover={{ background: 'rgba(37,99,235,0.08)', borderColor: 'rgba(59,130,246,0.25)', transition: { duration: 0.2 } }}
              >
                <div className="text-3xl font-extrabold text-blue-400 mb-1">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>

        </div>
      </section>

    
      {/* ── Technologies Section ── */}
      <section className="relative py-24 bg-gray-950 overflow-hidden">
        <FloatingShapes />

        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />

        <div className="relative z-10 container mx-auto px-6 lg:px-16">

          {/* Header */}
          <div className="text-center mb-16">
            <motion.span
              className="inline-block text-xs font-bold tracking-[0.25em] uppercase px-4 py-1.5 rounded-full mb-6"
              style={{ background: 'rgba(37,99,235,0.12)', border: '1px solid rgba(59,130,246,0.25)', color: '#93c5fd' }}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Tech Stack
            </motion.span>
            <motion.h2
              className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Technologies We <span className="text-blue-400">Use</span>
            </motion.h2>
            <motion.p
              className="text-gray-400 text-lg max-w-xl mx-auto"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              We work with modern, battle-tested technologies to build fast, scalable, and reliable solutions.
            </motion.p>
          </div>

          {/* Tech Groups */}
          {[
            {
              category: 'Frontend',
              color: 'blue',
              techs: [
                { name: 'React.js',      emoji: '⚛️' },
                { name: 'React Native',  emoji: '📱' },
                { name: 'JavaScript',    emoji: '🟨' },
                { name: 'HTML5',         emoji: '🌐' },
                { name: 'Tailwind CSS',  emoji: '🎨' },
                { name: 'Bootstrap',     emoji: '🅱️' },
                { name: 'NativeWind',    emoji: '💨' },
              ],
            },
            {
              category: 'Backend',
              color: 'purple',
              techs: [
                { name: '.NET',              emoji: '🔷' },
                { name: 'ASP.NET Core MVC',  emoji: '🏗️' },
                { name: 'Node.js',           emoji: '🟢' },
                { name: 'Java',              emoji: '☕' },
              ],
            },
            {
              category: 'Database',
              color: 'cyan',
              techs: [
                { name: 'MySQL',       emoji: '🐬' },
                { name: 'MongoDB',     emoji: '🍃' },
                { name: 'Supabase',    emoji: '⚡' },
                { name: 'PostgreSQL',  emoji: '🐘' },
              ],
            },
            {
              category: 'DevOps',
              color: 'orange',
              techs: [
                { name: 'Docker',          emoji: '🐳' },
                { name: 'Kubernetes',      emoji: '☸️' },
                { name: 'Minikube',        emoji: '🔬' },
                { name: 'AWS',             emoji: '☁️' },
                { name: 'Grafana',         emoji: '📊' },
                { name: 'Prometheus',      emoji: '🔥' },
                { name: 'OpenTelemetry',   emoji: '📡' },
                { name: 'OpenSearch',      emoji: '🔍' },
              ],
            },
            {
              category: 'Design',
              color: 'pink',
              techs: [
                { name: 'Figma', emoji: '🎭' },
              ],
            },
            {
              category: 'Tools',
              color: 'green',
              techs: [
                { name: 'Git',                emoji: '🌿' },
                { name: 'Figma',              emoji: '🎭' },
                { name: 'REST APIs',          emoji: '🔌' },
                { name: 'System Architecture',emoji: '🏛️' },
              ],
            },
          ].map((group, gi) => {
            const colorMap = {
              blue:   { badge: 'bg-blue-500/15 text-blue-300 border-blue-500/30',     card: 'rgba(37,99,235,0.12)',   cardHover: 'rgba(37,99,235,0.2)',   ring: 'rgba(59,130,246,0.35)'  },
              purple: { badge: 'bg-purple-500/15 text-purple-300 border-purple-500/30', card: 'rgba(124,58,237,0.12)', cardHover: 'rgba(124,58,237,0.2)',  ring: 'rgba(139,92,246,0.35)' },
              cyan:   { badge: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',     card: 'rgba(6,182,212,0.12)',   cardHover: 'rgba(6,182,212,0.2)',   ring: 'rgba(6,182,212,0.35)'  },
              orange: { badge: 'bg-orange-500/15 text-orange-300 border-orange-500/30', card: 'rgba(234,88,12,0.12)', cardHover: 'rgba(234,88,12,0.2)',   ring: 'rgba(234,88,12,0.35)'  },
              pink:   { badge: 'bg-pink-500/15 text-pink-300 border-pink-500/30',     card: 'rgba(219,39,119,0.12)', cardHover: 'rgba(219,39,119,0.2)',  ring: 'rgba(219,39,119,0.35)' },
              green:  { badge: 'bg-green-500/15 text-green-300 border-green-500/30',  card: 'rgba(22,163,74,0.12)',  cardHover: 'rgba(22,163,74,0.2)',   ring: 'rgba(22,163,74,0.35)'  },
            };
            const c = colorMap[group.color];
            return (
              <motion.div
                key={gi}
                className="mb-10 last:mb-0"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: gi * 0.1 }}
              >
                {/* Category label + divider */}
                <div className="flex items-center gap-3 mb-5">
                  <span className={`text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full border whitespace-nowrap ${c.badge}`}>
                    {group.category}
                  </span>
                  <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />
                </div>

                {/* Tech pill cards */}
                <div className="flex flex-wrap gap-3">
                  {group.techs.map(({ name, emoji }, ti) => (
                    <motion.div
                      key={ti}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl cursor-default select-none"
                      style={{
                        background: c.card,
                        border: `1px solid ${c.ring}`,
                      }}
                      initial={{ opacity: 0, scale: 0.85 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.35, delay: gi * 0.05 + ti * 0.05 }}
                      whileHover={{
                        background: c.cardHover,
                        scale: 1.07,
                        boxShadow: `0 4px 20px ${c.ring}`,
                        transition: { duration: 0.2 },
                      }}
                      whileTap={{ scale: 0.96 }}
                    >
                      <span className="text-base leading-none">{emoji}</span>
                      <span className="text-sm font-semibold text-gray-200 whitespace-nowrap">{name}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            );
          })}

        </div>
      </section>

      <footer className="bg-gray-950 text-gray-300 py-6 text-center">
        <p>© 2025 CmCoreTech. All rights reserved.</p>
        <p className="mt-2">Contact: cmcoretech@gmail.com | +91 87806 43219</p>
      </footer>
      {/* ══ BACK TO TOP ══ */}
      <motion.button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
        style={{ background: 'linear-gradient(135deg, #2563eb, #7c3aed)' }}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: showTop ? 1 : 0, scale: showTop ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        whileHover={{ scale: 1.15, boxShadow: '0 0 20px rgba(99,102,241,0.6)' }}
        whileTap={{ scale: 0.9 }}
        aria-label="Back to top"
      >
        <ChevronUp className="w-5 h-5 text-white" />
      </motion.button>

    </div>
  );
};

export default Solutions;
